import { createListenerMiddleware, TypedStartListening, UnknownAction } from "@reduxjs/toolkit";
import { AppDispatch, RootState } from "src/redux/store";
import { logDev } from "src/utils";
import { removeFromDrawerStack } from "./drawerStackSlice";

interface DrawerAction {
    type: string;
    payload: { id: string; [key: string]: any };
}

export const getDrawerTypeFromAction = (action: UnknownAction): keyof RootState => {
    return action.type.split("/")[0] as keyof RootState;
};

const rehydrateDrawerState = (api: any, drawerType: string, instanceData: any) => {
    api.dispatch({ type: `${drawerType}/rehydrateState`, payload: instanceData });
};

const DEBUG_STACKABLE_DRAWER_MIDDLEWARE = false;

export const drawerMiddleware = createListenerMiddleware();

type AppStartListening = TypedStartListening<RootState, AppDispatch>;

const startAppListening = drawerMiddleware.startListening as AppStartListening;

// In the show action middleware:
startAppListening({
    predicate: (action: any): action is DrawerAction => {
        const validShowActions = [
            "lotAndContainerDetails/showLotAndContainerDetails",
            "medicationConfigDrawer/showMedicationConfigDrawer",
            "medicationConfigDrawer/createNewMedication",
            "containerEntryDrawer/startContainerEntry",
            "containerEntryDrawer/startSignatureRequestTask/fulfilled", // async thunk
            "containerLogDrawer/createNewLogEntry",
            "containerLogDrawer/openContainerLogDrawer/fulfilled", // async thunk
            "containerLogDrawer/startContainerLogSignature/fulfilled", // async thunk
            "containerLogDrawer/initializeLogEntryWithAppointment/fulfilled", // async thunk
            "containerLogDrawer/checkoutSpecificContainer/fulfilled", // async thunk
        ];
        if (
            DEBUG_STACKABLE_DRAWER_MIDDLEWARE &&
            [
                "lotAndContainerDetails",
                "medicationConfigDrawer",
                "containerEntryDrawer",
                "containerLogDrawer",
            ].includes(action.type.split("/")[0])
        ) {
            logDev("NNN Show Drawer Predicate:", {
                action,
                type: action.type,
                isValid: validShowActions.includes(action.type),
            });
        }
        return validShowActions.includes(action.type);
    },
    effect: async (action, api) => {
        const drawerType = getDrawerTypeFromAction(action);
        const currentState = api.getState();
        const drawerStack = currentState.drawerStack.activeDrawers;
        const currentDrawer = currentState[drawerType];
        if (DEBUG_STACKABLE_DRAWER_MIDDLEWARE) {
            logDev("NNN Show Drawer Effect:", {
                action,
                drawerType,
                currentStack: drawerStack,
                currentDrawer,
            });
        }
        // Update the state of the previous drawer in the stack to capture any changes
        if (drawerStack.length > 0) {
            const lastDrawer = drawerStack[drawerStack.length - 1];
            // Update the drawer in the stack with its current state
            api.dispatch({
                type: "drawerStack/updateDrawerState",
                payload: {
                    id: lastDrawer.id,
                    state: currentState[lastDrawer.type],
                },
            });
        }
        // Add new drawer to stack
        if (!drawerStack.find((d) => d.id === currentDrawer.id) && currentDrawer.id) {
            api.dispatch({
                type: "drawerStack/addToDrawerStack",
                payload: {
                    id: currentDrawer.id,
                    type: drawerType,
                    state: currentDrawer,
                    parentId: (action.payload as any).parentId,
                },
            });
        } else if (drawerStack.find((d) => d.id === currentDrawer.id)) {
            api.dispatch({ type: "drawerStack/bringToFront", payload: currentDrawer.id });
        }
    },
});

// In the hide action middleware:
startAppListening({
    predicate: (action: any): action is DrawerAction => {
        const validHideActions = [
            "lotAndContainerDetails/hideLotAndContainerDetails",
            "medicationConfigDrawer/hideMedicationConfigDrawer",
            "medicationConfigDrawer/saveMedication/fulfilled", // async thunk
            "containerEntryDrawer/hideContainerEntryDrawer",
            "containerEntryDrawer/submitContainerEntryWithSignatureTask/fulfilled", // async thunk
            "containerEntryDrawer/submitSignatureRequestTask/fulfilled", // async thunk
            "containerLogDrawer/submitSignatureRequestTask/fulfilled", // async thunk
            "containerLogDrawer/createLogEntries/fulfilled", // async thunk
            "containerLogDrawer/hideContainerLogDrawer",
        ];
        if (
            DEBUG_STACKABLE_DRAWER_MIDDLEWARE &&
            [
                "lotAndContainerDetails",
                "medicationConfigDrawer",
                "containerEntryDrawer",
                "containerLogDrawer",
            ].includes(action.type.split("/")[0])
        ) {
            logDev("NNN Hide Drawer Predicate:", {
                action,
                type: action.type,
                isValid: validHideActions.includes(action.type),
            });
        }
        const isValid = validHideActions.includes(action.type);
        return isValid;
    },
    effect: async (action, api) => {
        const state = api.getState();
        const drawerType = getDrawerTypeFromAction(action);
        const currentDrawer = state[drawerType];
        if (DEBUG_STACKABLE_DRAWER_MIDDLEWARE) {
            logDev("NNN Hide Drawer Effect - Initial:", {
                action,
                drawerType,
                currentDrawer,
                currentStack: state.drawerStack.activeDrawers,
            });
        }
        if (currentDrawer && "id" in currentDrawer) {
            // Reset the current drawer state because it's being closed
            api.dispatch({ type: `${drawerType}/resetState` });

            // Remove the current drawer from the stack
            api.dispatch(removeFromDrawerStack(currentDrawer.id));
            // Get updated state after removal
            const updatedState = api.getState();
            const activeDrawers = updatedState.drawerStack.activeDrawers;
            // If there are remaining drawers, rehydrate the last one
            if (activeDrawers.length > 0) {
                const previousDrawer = activeDrawers[activeDrawers.length - 1];
                // First reset drawer state by dispatching its hide action
                api.dispatch({ type: `${previousDrawer.type}/resetState` });
                if (DEBUG_STACKABLE_DRAWER_MIDDLEWARE) {
                    logDev("NNN Hide Drawer Effect - Rehydrating:", {
                        previousDrawer,
                        drawerState: previousDrawer.state,
                        rehydratePayload: {
                            ...previousDrawer.state,
                            id: previousDrawer.id,
                            show: true,
                        },
                    });
                }
                // Then rehydrate with previous state
                api.dispatch({
                    type: `${previousDrawer.type}/rehydrateState`,
                    payload: { ...previousDrawer.state, id: previousDrawer.id, show: true },
                });
            }
        }
    },
});

// Handle bring to front actions
startAppListening({
    predicate: (action: any) => action.type === "drawerStack/bringToFront",
    effect: async (action, api) => {
        const state = api.getState();
        const drawerStack = state.drawerStack.activeDrawers;
        const drawer = drawerStack.find((d) => d.id === action.payload);

        if (drawer) {
            rehydrateDrawerState(api, drawer.type, drawer.state.instanceData);
        }
    },
});
